import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useTransactionFiles from 'Hooks/useTransactionFiles';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { getTransactionFileDownloadUrl } from 'Services/transactions';
import InputFile from 'Components/Atoms/InputFile/InputFile';
import File from 'Components/Molecules/File/File';

const download = (url, fileName) => {
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const MAX_FILE_SIZE = 100 * 1000 * 1000;

export default function TransactionEditorDrawerFiles(props) {
    const { transactionId, onToggleUploading } = props;

    const { t } = useTranslation('transactions');
    const isFirstRender = useRef(true);
    const [uploadError, setUploadError] = useState(null);
    const { showUnknownError } = useErrors();

    const { files, isUpdating, uploadFiles, deleteFile } = useTransactionFiles(transactionId);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        onToggleUploading(isUpdating);
    }, [isUpdating]);

    useEffect(() => {
        return () => {
            onToggleUploading(false);
        };
    }, []);

    useEffect(() => {
        if (uploadError) {
            setTimeout(() => {
                setUploadError(null);
            }, 5000);
        }
    }, [uploadError]);

    const handleClickDownloadFile = async (file) => {
        try {
            const { download_url } = await getTransactionFileDownloadUrl(transactionId, file.id);
            download(download_url, file.name);
        } catch (error) {
            showUnknownError(error);
        }
    };

    return (
        <div className="pt-6">
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">{t('attachments')}</div>
            <div className="empty:hidden grid gap-2 mb-4">
                {files.map((file) => (
                    <File
                        loading={file.isPlaceholder}
                        key={file.id}
                        file={file}
                        onDownload={() => handleClickDownloadFile(file)}
                        onDelete={() => deleteFile(file.id)}
                    />
                ))}
            </div>

            <InputFile
                label={t('drop-the-file-here-to-add-it')}
                multiple
                onUpload={uploadFiles}
                maxFileSize={MAX_FILE_SIZE}
            />
        </div>
    );
}

TransactionEditorDrawerFiles.propTypes = {
    transactionId: PropTypes.string.isRequired,
    invoicesIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onToggleUploading: PropTypes.func,
};
